import React from "react";

import { Navigate } from "react-router-dom";

import { usePermissions } from "@/features/rbac/hooks/usePermissions";
import type { PermissionOrWildcard } from "@/features/rbac/permissions/catalog";

interface PermissionRouteProps {
  children: React.ReactNode;
  /** Permiso o permisos requeridos para entrar. */
  permission: PermissionOrWildcard | readonly PermissionOrWildcard[];
  /** Con una lista: `any` exige uno cualquiera, `all` exige todos. */
  mode?: "any" | "all";
  /** A dónde redirigir cuando se deniega. */
  redirectTo?: string;
}

/**
 * Protege una ruta con un permiso del catálogo.
 *
 * Antes cada ruta traía su propio array de valores de `permission_admin` y este
 * componente definía por su cuenta qué era un superadministrador. Ahora delega
 * en el único resolver: quién es superadministrador se decide en los roles, no
 * aquí.
 *
 * @example
 * ```tsx
 * <PermissionRoute permission="users:list">
 *   <Users />
 * </PermissionRoute>
 * ```
 */
export function PermissionRoute({
  children,
  permission,
  mode = "any",
  redirectTo = "/dashboard",
}: PermissionRouteProps) {
  const { can, canAll, canAny, isLoading } = usePermissions();

  // Mientras se resuelven los permisos no se decide nada: renderizar
  // optimista mostraría por un instante una vista prohibida.
  if (isLoading) {
    return null;
  }

  const list = Array.isArray(permission)
    ? (permission as readonly PermissionOrWildcard[])
    : null;

  const granted = list
    ? mode === "all"
      ? canAll(list)
      : canAny(list)
    : can(permission as PermissionOrWildcard);

  if (!granted) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
